import * as React from 'react';
import { Suspense, useEffect } from 'react';
import {
    Route,
    Navigate,
    useLocation,
    RouterProvider,
    createBrowserRouter,
    createRoutesFromElements,
} from 'react-router-dom';
import './App.css';
import AuthProvider from './Authentication/components/AuthProvider';
import PersistLogin from './Authentication/components/PersistLogin';
import RequireAuth from './Authentication/components/RequireAuth';
import Layout from './Common/components/Layout';
import PublicFeatureFlags from './Features/components/PublicFeatureFlags';
import Loading from './Common/components/Loading';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import persistedStore from '../src/ExperienceBuilder/utils/experienceStore';
import 'react-day-picker/dist/style.css';
import { getAnalytics, logEvent } from 'firebase/analytics';

// Lazy-loaded route components
const Home = React.lazy(() => import('./Home/components/Home'));
const CommandCenter = React.lazy(() => import('./CommandCenter/CommandCenter'));
const Accounts = React.lazy(() => import('./Accounts/components/Accounts'));
const CreateAccount = React.lazy(() => import('./Accounts/components/CreateAccount'));
const EditAccount = React.lazy(() => import('./Accounts/components/EditAccount'));
const SetPassword = React.lazy(() => import('./Accounts/components/SetPassword'));
const Login = React.lazy(() => import('./Authentication/components/Login'));
const Activations = React.lazy(() => import('./Activations/components/Activations'));
const CreateActivation = React.lazy(() => import('./Activations/components/CreateActivation'));
const EditActivation = React.lazy(() => import('./Activations/components/EditActivation'));
const ActivationDashboard = React.lazy(() => import('./Activations/components/ActivationDashboard'));
const EditGallery = React.lazy(() => import('./GalleryConfiguration/components/EditGallery'));
const UploadToActivation = React.lazy(() => import('./Activations/components/UploadToActivation'));
const Gallery = React.lazy(() => import('./Gallery/components/Gallery'));
const DeviceKeys = React.lazy(() => import('./DeviceManagement/components/DeviceKeys'));
const DeviceGroups = React.lazy(() => import('./DeviceManagement/components/DeviceGroups'));
const CreateDeviceKey = React.lazy(() => import('./DeviceManagement/components/CreateDeviceKey'));
const ShareManager = React.lazy(() => import('./Activations/components/ShareManager'));
const UploadTemplate = React.lazy(() => import('./EmailTemplates/components/UploadTemplate'));
const EmailTemplates = React.lazy(() => import('./EmailTemplates/components/Templates'));
const GalleryPhoto = React.lazy(() => import('./Gallery/components/GalleryPhoto'));
const PersonalGallery = React.lazy(() => import('./Gallery/components/PersonalGallery'));
const ShareLanding = React.lazy(() => import('./Share/components/ShareLanding'));
const FeatureFlags = React.lazy(() => import('./Features/components/FeatureFlags'));
const EditFeatureFlag = React.lazy(() => import('./Features/components/EditFeatureFlag'));
const CreateFeatureFlag = React.lazy(() => import('./Features/components/CreateFeatureFlag'));
const EditRoleFeatureFlagAssociation = React.lazy(() => import('./Features/components/EditRoleFeatureFlagAssociation'));
const V3Sync = React.lazy(() => import('./Activations/components/V3Sync'));
const GalleryDirectory = React.lazy(() => import('./Gallery/components/GalleryDirectory'));
const EditCrowdGallery = React.lazy(() => import('./GalleryConfiguration/components/EditCrowdGallery'));
const CreateProcessingFlow = React.lazy(() => import('./Processing/components/CreateProcessingFlow'));
const EditProcessingFlow = React.lazy(() => import('./Processing/components/EditProcessingFlow'));
const DBOfSmiles = React.lazy(() => import('./Gallery/components/dbofsmiles/DBOfSmiles').then(m => ({ default: m.DBOfSmiles })));
const FavoritesGallery = React.lazy(() => import('./Gallery/components/favorites/FavoritesGallery').then(m => ({ default: m.FavoritesGallery })));
const SessionGallery = React.lazy(() => import('./Gallery/components/SessionGallery'));
const SessionSharePage = React.lazy(() => import('./Gallery/components/SessionSharePage'));
const SessionManager = React.lazy(() => import('./Activations/components/SessionManager'));
const SharingStation = React.lazy(() => import('./Gallery/components/SharingStation'));
const Slideshow = React.lazy(() => import('./Gallery/components/Slideshow'));
const CreateDeviceGroup = React.lazy(() => import('./DeviceManagement/components/CreateDeviceGroup'));
const CreateDeviceInvite = React.lazy(() => import('./DeviceManagement/components/CreateDeviceInvite'));
const DeviceInvites = React.lazy(() => import('./DeviceManagement/components/DeviceInvites'));
const GroupDeviceKeys = React.lazy(() => import('./DeviceManagement/components/GroupDeviceKeys'));
const JobsDashboard = React.lazy(() => import('./Jobs/components/JobsDashboard'));
const JobDetail = React.lazy(() => import('./Jobs/components/JobDetail'));
const JobEditor = React.lazy(() => import('./Jobs/components/JobEditor'));
const AdminDocumentFrame = React.lazy(() => import('./Documents/components/AdminDocumentFrame'));
const CreateJob = React.lazy(() => import('./Jobs/components/CreateJob'));
const DeviceManagement = React.lazy(() => import('./DeviceManagement/components/DeviceManagement'));
const OrgSettings = React.lazy(() => import('./Orgs/components/OrgSettings'));
const PicTimeRedirectHandler = React.lazy(() => import('./Orgs/components/PicTimeRedirectHandler'));
const CollageWall = React.lazy(() => import('./Gallery/components/t4agt/CollageWall').then(m => ({ default: m.CollageWall })));
const AnimatedCollageWall = React.lazy(() => import('./Gallery/components/t4agt/AnimatedCollageWall').then(m => ({ default: m.AnimatedCollageWall })));
const EditFTPSettings = React.lazy(() => import('./GalleryConfiguration/components/EditFTPSettings'));
const OrgsList = React.lazy(() => import('./Orgs/components/OrgsList'));
const OrgCreate = React.lazy(() => import('./Orgs/components/OrgCreate'));
const OrgAdministration = React.lazy(() => import('./Orgs/components/OrgAdministration'));
const OrgAccountCreate = React.lazy(() => import('./Orgs/components/OrgAccountCreate'));
const PageNotFound = React.lazy(() => import('./Common/components/PageNotFound'));
const SingleDestinationActivationGallery = React.lazy(() => import('./Gallery/components/SingleDestinationActivationGallery'));
const SystemHealth = React.lazy(() => import('./SystemHealth/SystemHealth'));
const SessionCreate = React.lazy(() => import('./Activations/components/SessionCreate'));
const PaymentLanding = React.lazy(() => import('./Gallery/components/payments/PaymentLandingPage'));
const CreateLicenseType = React.lazy(() => import('./Licenses/components/CreateLicenseType'));
const EditLicenseType = React.lazy(() => import('./Licenses/components/EditLicenseType'));
const LicenseTypes = React.lazy(() => import('./Licenses/components/LicenseTypes'));
const EditLicenseTypeFeatures = React.lazy(() => import('./Licenses/components/EditLicenseTypeFeatures'));
const OrgLicenses = React.lazy(() => import('./Licenses/components/OrgLicenses'));
const CreateLicense = React.lazy(() => import('./Licenses/components/CreateLicense'));
const DeviceTelemetryView = React.lazy(() => import('./DeviceTelemetry').then(m => ({ default: m.DeviceTelemetryView })));
const DeepLocal = React.lazy(() => import('./Gallery/components/DeepLocal'));
const PhotoEngineerStats = React.lazy(() => import('./PhotoEngineers/components/PhotoEngineerStats'));
const TemplateList = React.lazy(() => import('./Templates').then(m => ({ default: m.TemplateList })));
const TemplateForm = React.lazy(() => import('./Templates').then(m => ({ default: m.TemplateForm })));
const TemplateDetail = React.lazy(() => import('./Templates').then(m => ({ default: m.TemplateDetail })));
const AnnouncementList = React.lazy(() => import('./Announcements').then(m => ({ default: m.AnnouncementList })));
const AnnouncementForm = React.lazy(() => import('./Announcements').then(m => ({ default: m.AnnouncementForm })));
const AnnouncementDetail = React.lazy(() => import('./Announcements').then(m => ({ default: m.AnnouncementDetail })));
const GlobalSettingsForm = React.lazy(() => import('./GlobalSettings').then(m => ({ default: m.GlobalSettingsForm })));
const WhitelabelSettings = React.lazy(() => import('./WhitelabelSettings').then(m => ({ default: m.WhitelabelSettings })));
const WhitelabelSettingsForm = React.lazy(() => import('./WhitelabelSettings').then(m => ({ default: m.WhitelabelSettingsForm })));
const WhitelabelSettingsDetail = React.lazy(() => import('./WhitelabelSettings').then(m => ({ default: m.WhitelabelSettingsDetail })));
const AssignDeviceKey = React.lazy(() => import('./DeviceManagement/components/AssignDeviceKey'));
const GalleryUserList = React.lazy(() => import('./GalleryUsers/GalleryUserList'));

let { store, persistor } = persistedStore();

const ROLES = {
    Admin: 0,
    User: 1,
    Client: 2,
    GlobalAdmin: 69,
};

function SuspenseWrapper({ children }: { children: React.ReactNode }) {
    return (
        <Suspense fallback={<Loading isLoading={true} />}>
            {children}
        </Suspense>
    );
}

function lazy(Component: React.LazyExoticComponent<any>) {
    return (
        <SuspenseWrapper>
            <Component />
        </SuspenseWrapper>
    );
}

const router = createBrowserRouter(
    createRoutesFromElements(
        <>
            {/* @ts-ignore */}
            <Route element={<PublicFeatureFlags />}>
                <Route path="/gallery" element={lazy(GalleryDirectory)} />
                <Route path="/db-of-smiles" element={lazy(DBOfSmiles)} />
                <Route
                    path="/collage/:activationId"
                    element={lazy(CollageWall)}
                />
                <Route
                    path="/animated-collage/:activationId"
                    element={lazy(AnimatedCollageWall)}
                />
                <Route path="/gallery/:slug" element={lazy(Gallery)} />
                <Route
                    path="/gallery/:slug/user/:destinationSlug"
                    element={lazy(SingleDestinationActivationGallery)}
                />
                <Route
                    path="/gallery/:slug/sharing"
                    element={lazy(SharingStation)}
                />
                <Route path="/gallery/:slug/dl" element={lazy(DeepLocal)} />
                <Route
                    path="/gallery/:slug/session-payment/:token"
                    element={lazy(PaymentLanding)}
                />
                <Route
                    path="/gallery/:slug/slideshow"
                    element={lazy(Slideshow)}
                />
                <Route
                    path="/session/:galleryId/:photoSessionId"
                    element={lazy(SessionGallery)}
                />
                <Route
                    path="/session/:galleryId/:photoSessionId/share"
                    element={lazy(SessionSharePage)}
                />
                <Route path="/photos/:slug" element={lazy(PersonalGallery)} />
                <Route path="/photo/:galleryId" element={lazy(GalleryPhoto)} />
                <Route
                    path="/gallery/:galleryId/share"
                    element={lazy(ShareLanding)}
                />
                <Route path="/login" element={lazy(Login)} />
            </Route>
            <Route element={<PersistLogin />}>
                <Route
                    element={
                        <RequireAuth
                            allowedRoles={[
                                ROLES.Admin,
                                ROLES.User,
                                ROLES.GlobalAdmin,
                            ]}
                        />
                    }
                >
                    <Route path="/" element={<Layout />}>
                        <Route path="home" element={lazy(Home)} />
                        <Route
                            path="command-center"
                            element={lazy(CommandCenter)}
                        />

                        <Route path="accounts" element={lazy(Accounts)} />
                        <Route
                            path="favorites"
                            element={lazy(FavoritesGallery)}
                        />
                        <Route path="accounts/:id" element={lazy(EditAccount)} />
                        <Route
                            path="accounts/:id/password"
                            element={lazy(SetPassword)}
                        />
                        <Route
                            path="accounts/create"
                            element={lazy(CreateAccount)}
                        />
                        <Route path="activations" element={lazy(Activations)} />
                        <Route
                            path="activations/create"
                            element={lazy(CreateActivation)}
                        />

                        <Route path="activations/v3sync" element={lazy(V3Sync)} />

                        <Route
                            path="activations/:id/edit"
                            element={lazy(EditActivation)}
                        />

                        <Route
                            path="activations/:activationId"
                            element={lazy(ActivationDashboard)}
                        />

                        <Route
                            path="activations/:id/upload"
                            element={lazy(UploadToActivation)}
                        />

                        <Route
                            path="activations/:id/gallery/:galleryId/edit"
                            element={lazy(EditGallery)}
                        />

                        <Route
                            path="activations/:activationId/sessions/create"
                            element={lazy(SessionCreate)}
                        />

                        <Route
                            path="activations/:activationId/sessions/:photoSessionId"
                            element={lazy(SessionManager)}
                        />

                        <Route
                            path="activations/:activationId/manage-shares"
                            element={lazy(ShareManager)}
                        />

                        <Route
                            path="activations/:activationId/galleryConfiguration/:galleryId"
                            element={lazy(EditGallery)}
                        />

                        <Route
                            path="activations/:activationId/gallery/:galleryId/edit/crowdGallery"
                            element={lazy(EditCrowdGallery)}
                        />

                        <Route
                            path="activations/:activationId/gallery/:galleryId/edit/ftp"
                            element={lazy(EditFTPSettings)}
                        />

                        <Route
                            path="activations/:activationId/gallery/:galleryId/processing/create"
                            element={lazy(CreateProcessingFlow)}
                        />

                        <Route
                            path="activations/:activationId/gallery/:galleryId/processing/edit/:flowId"
                            element={lazy(EditProcessingFlow)}
                        />

                        <Route
                            path="activations/:activationId/documents/:documentId"
                            element={lazy(AdminDocumentFrame)}
                        />

                        <Route
                            path="device-management/device-groups"
                            element={lazy(DeviceGroups)}
                        />

                        <Route
                            path="device-management/device-groups/create"
                            element={lazy(CreateDeviceGroup)}
                        />

                        <Route
                            path="device-management/device-groups/:groupId/invites"
                            element={lazy(DeviceInvites)}
                        />

                        <Route
                            path="device-management/device-invites/create"
                            element={lazy(CreateDeviceInvite)}
                        />

                        <Route
                            path="device-management"
                            element={lazy(DeviceManagement)}
                        />
                        <Route path="device-keys" element={lazy(DeviceKeys)} />

                        <Route
                            path="device-management/device-groups/:groupId/device-keys"
                            element={lazy(GroupDeviceKeys)}
                        />

                        <Route
                            path="device-management/device-groups/:groupId/device-keys/assign"
                            element={lazy(AssignDeviceKey)}
                        />

                        <Route
                            path="device-management/device-groups/:groupId/device-keys/create"
                            element={lazy(CreateDeviceKey)}
                        />

                        <Route
                            path="device-management/device-keys/create"
                            element={lazy(CreateDeviceKey)}
                        />

                        <Route path="/templates" element={lazy(EmailTemplates)} />

                        <Route
                            path="templates/import"
                            element={lazy(UploadTemplate)}
                        />

                        <Route
                            path="org/whitelabel-settings"
                            element={lazy(WhitelabelSettings)}
                        />

                        <Route
                            path="org/whitelabel-settings/new"
                            element={lazy(WhitelabelSettingsForm)}
                        />

                        <Route
                            path="org/whitelabel-settings/:id"
                            element={lazy(WhitelabelSettingsDetail)}
                        />

                        <Route
                            path="org/whitelabel-settings/:id/edit"
                            element={lazy(WhitelabelSettingsForm)}
                        />

                        <Route
                            path="admin/templates"
                            element={lazy(TemplateList)}
                        />

                        <Route
                            path="admin/templates/new"
                            element={lazy(TemplateForm)}
                        />

                        <Route
                            path="admin/templates/:id"
                            element={lazy(TemplateDetail)}
                        />

                        <Route
                            path="admin/templates/:id/edit"
                            element={lazy(TemplateForm)}
                        />

                        <Route
                            path="admin/features"
                            element={lazy(FeatureFlags)}
                        />

                        <Route
                            path="admin/features/create"
                            element={lazy(CreateFeatureFlag)}
                        />

                        <Route
                            path="admin/features/:id"
                            element={lazy(EditFeatureFlag)}
                        />

                        <Route
                            path="admin/features/role/:name/:id"
                            element={lazy(EditRoleFeatureFlagAssociation)}
                        />

                        <Route
                            path="admin/licenses"
                            element={lazy(LicenseTypes)}
                        />

                        <Route
                            path="admin/licenses/create"
                            element={lazy(CreateLicenseType)}
                        />

                        <Route
                            path="admin/licenses/:id"
                            element={lazy(EditLicenseType)}
                        />

                        <Route
                            path="admin/licenses/:licenseTypeId/features"
                            element={lazy(EditLicenseTypeFeatures)}
                        />

                        <Route
                            path="admin/licenses/org/:orgId"
                            element={lazy(OrgLicenses)}
                        />

                        <Route
                            path="admin/licenses/create-license"
                            element={lazy(CreateLicense)}
                        />

                        <Route path="admin/orgs" element={lazy(OrgsList)} />

                        <Route
                            path="admin/orgs/:orgId"
                            element={lazy(OrgAdministration)}
                        />

                        <Route
                            path="admin/orgs/create"
                            element={lazy(OrgCreate)}
                        />

                        <Route
                            path="admin/orgs/:orgId/account/create"
                            element={lazy(OrgAccountCreate)}
                        />

                        <Route
                            path="admin/system-health"
                            element={lazy(SystemHealth)}
                        />

                        <Route
                            path="admin/photo-engineers/stats"
                            element={lazy(PhotoEngineerStats)}
                        />

                        <Route
                            path="admin/announcements"
                            element={lazy(AnnouncementList)}
                        />

                        <Route
                            path="admin/announcements/new"
                            element={lazy(AnnouncementForm)}
                        />

                        <Route
                            path="admin/announcements/:id"
                            element={lazy(AnnouncementDetail)}
                        />

                        <Route
                            path="admin/announcements/:id/edit"
                            element={lazy(AnnouncementForm)}
                        />

                        <Route
                            path="admin/global-settings"
                            element={lazy(GlobalSettingsForm)}
                        />

                        <Route path="jobs" element={lazy(JobsDashboard)} />

                        <Route path="jobs/:id" element={lazy(JobDetail)} />
                        <Route path="jobs/:id/edit" element={lazy(JobEditor)} />

                        <Route
                            path="admin/gallery-users"
                            element={lazy(GalleryUserList)}
                        />

                        <Route path="jobs/create" element={lazy(CreateJob)} />

                        <Route
                            path="jobs/:jobId/activation/create"
                            element={lazy(CreateActivation)}
                        />

                        <Route path="org" element={lazy(OrgSettings)} />

                        <Route
                            path="integrations/pic-time/redirect"
                            element={lazy(PicTimeRedirectHandler)}
                        />

                        <Route
                            path="/orgs/:orgId/device-telemetry/:deviceId"
                            element={lazy(DeviceTelemetryView)}
                        />

                        <Route path="/" element={<Navigate to="/home" />} />
                    </Route>
                </Route>
                <Route path="/" element={<Navigate to="/home" />} />

                <Route path="*" element={lazy(PageNotFound)} />
            </Route>
        </>,
    ),
);

export default function App() {
    return (
        <Provider store={store}>
            <PersistGate loading={null} persistor={persistor}>
                <AuthProvider>
                    <RouterProvider
                        router={router}
                        fallbackElement={<Loading isLoading={true} />}
                    />
                    <div className="App"></div>
                </AuthProvider>
            </PersistGate>
        </Provider>
    );
}

// eslint-disable-next-line
function FirebaseAnalytics() {
    const location = useLocation();
    useEffect(() => {
        const page_path = location.pathname + location.search;
        const analytics = getAnalytics();
        logEvent(analytics, 'page_view', { firebase_screen: page_path });
    }, [location]);
    return null;
}
